import { ApolloError } from 'apollo-client';
import { Loader } from '@googlemaps/js-api-loader';
import { useToast } from 'vue-toastification';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import i18n from '@/i18n';

dayjs.extend(LocalizedFormat);

const toast = useToast();
const { t } = i18n.global;

const dateLocaleOverride = process.env.VUE_APP_I18N_DATE_LOCALE;

declare const google: any;

export const preventError = (e: ApolloError): void => {
  const result = (e?.networkError as any)?.result;
  const graphQLError = e.graphQLErrors?.[0];
  const isUserInputError = graphQLError?.extensions?.code === 'BAD_USER_INPUT';
  const userError = isUserInputError ? graphQLError?.extensions?.error : undefined;
  const code: string = result?.error || userError || 'UNKNOWN_ERROR';

  if (code !== 'TRIAL_EXPIRED' && code !== 'INSTITUTION_DELETED') {
    const key = `errors.${code}`;
    if (i18n.global.te(key)) {
      toast.error(t(key));
    } else if (isUserInputError && graphQLError?.message) {
      // No translation registered for this validation error's code — fall back to the
      // server's own message rather than a generic "Unknown error" (BAD_USER_INPUT
      // messages are written to be shown to the user, see api's main.ts formatError).
      toast.error(graphQLError.message);
    } else {
      toast.error(t('errors.UNKNOWN_ERROR'));
    }
  }
};

type DateFormat = 'dateTime' | 'date' | 'time';

export const createDateTime = (
  date: string | Date | dayjs.Dayjs | null | undefined,
  format: DateFormat = 'dateTime',
): string | null => {
  if (!date) {
    return null;
  }
  const newDate = new Date(dayjs.isDayjs(date) ? date.toDate() : date).toISOString();
  if (format === 'dateTime') {
    return newDate;
  } else if (format === 'date') {
    return newDate.split('T')[0];
  } else {
    return newDate.split('T')[1];
  }
};

export const formatNurseName = (
  nurse: { name?: string | null; email?: string | null } | null | undefined,
  fallback = '',
): string => {
  return nurse?.name || nurse?.email || fallback;
};

export const parseDate = (value: string | Date | dayjs.Dayjs | null | undefined): Date | null => {
  if (!value) {
    return null;
  }

  return dayjs(value).toDate();
};

export const formatDateTime = (
  start: string | Date | dayjs.Dayjs | null | undefined,
  end?: string | Date | dayjs.Dayjs | null | undefined,
  dateFormat = 'L',
  timeFormat = 'LT',
  locale?: string,
): string | null => {
  if (!start) {
    return null;
  }

  const currentLocale = dateLocaleOverride || locale;

  const startDayObj = currentLocale ? dayjs(start).locale(currentLocale) : dayjs(start);
  const startDate = startDayObj.format(dateFormat);
  const startTime = timeFormat ? startDayObj.format(timeFormat) : '';

  if (end) {
    const endDayObj = currentLocale ? dayjs(end).locale(currentLocale) : dayjs(end);
    const endDate = endDayObj.format(dateFormat);
    const endTime = endDayObj.format(timeFormat);

    if (startDate === endDate) {
      return `${startDate} ${startTime} - ${endTime}`;
    }

    return `${startDate} ${startTime} - ${endDate} ${endTime}`;
  }

  return `${startDate} ${startTime}`.trim();
};

export const exportCsv = async <TPayload extends Record<string, unknown>>(
  url: string,
  fileName: string,
  token: string | undefined,
  payload: TPayload,
): Promise<void> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.error(`HTTP error! Status: ${response.status}`);
      return;
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (data?.approvalRequest) {
        toast.info(t('approvalRequest.toasts.created'));
      }
      return;
    }

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (error: unknown) {
    console.error('Error:', (error as Error).message);
  }
};

export const initMap = async (): Promise<void> => {
  const loader = new Loader({
    apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY as string,
    version: 'weekly',
    libraries: ['maps', 'marker'],
  });
  loader.load().then(async () => {
    const { Map } = await google.maps.importLibrary('maps');
    return new Map(document.getElementById('map') as HTMLElement, {
      center: { lat: 59.436962, lng: 24.753574 },
      zoom: 12,
      mapId: 'TEST_MAP_ID',
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      disableDefaultUI: true,
    });
  });
};
